import * as Sentry from '@sentry/react'
import { useQuery } from '@tanstack/react-query'
import {
  Activity,
  Blocks,
  BookOpenText,
  Bot,
  Bug,
  ChartGantt,
  ChevronsUpDown,
  Cog,
  FileSignature,
  FileSpreadsheet,
  FileText,
  FolderArchive,
  GitMerge,
  Globe,
  IdCard,
  Layers,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  MapPin,
  Moon,
  Puzzle,
  ScanFace,
  Settings,
  Smartphone,
  Sun,
  Telescope,
  User,
  UserCog,
  Waypoints,
} from 'lucide-react'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { PulsingDot } from '@/components/investigations/_internal/PulsingDot'
import { StatusDot } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  NavigationRail,
  NavigationRailCaseChip,
  type NavigationRailItem,
  spaClick,
} from '@/components/ui/navigation-rail'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useAuth } from '@/contexts/AuthContext'
import { usePendingResolutionCount } from '@/features/entity-resolution/hooks/usePendingResolutionCount'
import { usePendingFaceMatchCount } from '@/features/face-matching/hooks/useFaceMatching'
import {
  useViewpointSnapshot,
  type ViewpointSnapshot,
} from '@/features/investigations/hooks/useViewpointSnapshot'
import { useMonitorUnreadCount } from '@/features/monitor/useMonitorInbox'
import { useCaseSchemaStatus } from '@/features/schema/hooks/useCaseSchemaStatus'
import { useTattooLeadCount } from '@/features/tattoo-leads/hooks/useTattooLeadCount'
import { useVoiceLeadCount } from '@/features/voice-leads/hooks/useVoiceLeadCount'
import { caseKeys, useCaseSettings } from '@/hooks/use-case-queries'
import {
  ADVANCED_FACE_DETECTION_EXTENSION_ID,
  HUMINT_EXTENSION_ID,
  MOBILE_FORENSICS_EXTENSION_ID,
  OSINT_EXTENSION_ID,
  useEnabledExtensionIds,
} from '@/hooks/use-extensions'
import { type ScrollEdges, useScrollEdges } from '@/hooks/use-scroll-edges'
import { hasMinRole, isAdmin } from '@/lib/roles'
import { api } from '@/services/api'
import { useTheme } from '@/theme'
import { RailBrand } from './RailBrand'
import { entityResolutionBadge } from './rail-badges'
import { HOME_ROUTE, SECTIONS, type Section, type SectionKey } from './sections'
import { hiddenSubViewPaths } from './workspaces'

interface CaseSummary {
  id: string
  name: string
}

interface Props {
  activeSection: SectionKey | undefined
  onSectionClick: (section: Section) => void
  onSectionHover: (section: Section | null) => void
  /** When set, the rail switches to case mode (case-chip + case items). */
  caseId?: string
  /** Path segment under `/cases/:caseId/` of the current sub-view. */
  activeSubViewPath?: string
}

// Icons + ordering for the global rail.
const RAIL_ICON: Partial<Record<SectionKey, React.ReactNode>> = {
  cases: <Layers />,
  data: <FolderArchive />,
  extensions: <Puzzle />,
  admin: <UserCog />,
  docs: <LifeBuoy />,
  settings: <Cog />,
}

const GROUP_END: Partial<Record<SectionKey, boolean>> = {
  extensions: true,
}

// Case-mode rail items. Overview sits at the top, then groups:
// Case data · Discovery · Investigations · Output (separators via `groupEnd`).
// `tKey` resolves to `nav.subviews.<key>` so labels follow the active locale.
// `extensionId` marks items that render only while that extension is enabled.
export type CaseItemSpec = Omit<NavigationRailItem, 'label'> & {
  tKey: string
  extensionId?: string
}

/** Pseudo rail id for the active-investigation chip — intercepted in the
 * select handler (navigates to the investigation, not to a route segment). */
const ACTIVE_INVESTIGATION_ID = 'active-investigation'

// Case layer — everything that belongs to the case itself, not to one line of
// inquiry: intake and resolution, then the discovery surfaces.
export const CASE_DATA_SPECS: CaseItemSpec[] = [
  // Top
  { id: 'overview', tKey: 'overview', icon: <LayoutDashboard /> },
  // What the case watches and the alerts it raised (PRD-0075 B).
  { id: 'monitor', tKey: 'monitor', icon: <Activity /> },
  { id: 'agent', tKey: 'agent', icon: <Bot />, groupEnd: true },
  // Case data
  { id: 'documents', tKey: 'documents', icon: <FileText /> },
  { id: 'schema', tKey: 'caseSchema', icon: <Blocks /> },
  { id: 'entity-resolution', tKey: 'entityResolution', icon: <GitMerge /> },
  { id: 'identity', tKey: 'identity', icon: <IdCard />, groupEnd: true },
  // Discovery — agents that surface candidates. Research works over the case's
  // own data, OSINT over live public sources; both feed the case, neither is
  // scoped to a single investigation.
  { id: 'search', tKey: 'search', icon: <Telescope /> },
  { id: 'osint', tKey: 'osint', icon: <Globe />, extensionId: OSINT_EXTENSION_ID },
  // Device evidence is a discovery surface too: it is browsed to find
  // candidates, exactly like Research and OSINT, and is not scoped to one
  // investigation. It closes the group — `enabledCaseDataSpecs` walks the
  // separator back to OSINT (or Research) when an extension is switched off.
  {
    id: 'comms',
    tKey: 'comms',
    icon: <Smartphone />,
    extensionId: MOBILE_FORENSICS_EXTENSION_ID,
  },
  // The HUMINT workspace (ADR-0047) opens in its own shell, outside this layout; its route
  // `/cases/:caseId/humint` lands on its Domov.
  { id: 'humint', tKey: 'humint', icon: <ScanFace />, extensionId: HUMINT_EXTENSION_ID },
  { id: 'structured-ingest', tKey: 'structuredIngest', icon: <FileSpreadsheet />, groupEnd: true },
]

/** The rail entries for the extensions this deployment has switched on, minus the
 * views the case's settings hide from the menu (their routes stay reachable).
 *
 * A dropped entry may have been the one closing its group (OSINT closes
 * Discovery), so its separator moves back to the last surviving item — the
 * group still closes, wherever the gap falls. */
function enabledCaseDataSpecs(
  enabled: ReadonlySet<string>,
  hidden: ReadonlySet<string> = new Set()
): CaseItemSpec[] {
  const kept: CaseItemSpec[] = []
  for (const spec of CASE_DATA_SPECS) {
    if ((spec.extensionId != null && !enabled.has(spec.extensionId)) || hidden.has(spec.id)) {
      const last = kept.length - 1
      if (spec.groupEnd && last >= 0) kept[last] = { ...kept[last], groupEnd: true }
      continue
    }
    kept.push(spec)
  }
  return kept
}

const INVESTIGATIONS_SPEC: CaseItemSpec = {
  id: 'investigations',
  tKey: 'investigations',
  icon: <BookOpenText />,
}

// Lenses on the working set — three projections of one object set. They exist
// only relative to a viewpoint, so they nest under the investigation chip when
// one is active and are hidden entirely when there is none.
const LENS_SPECS: CaseItemSpec[] = [
  { id: 'graph', tKey: 'graph', icon: <Waypoints /> },
  { id: 'geolocation', tKey: 'map', icon: <MapPin /> },
  { id: 'time-axis', tKey: 'timeline', icon: <ChartGantt /> },
]

const OUTPUT_SPECS: CaseItemSpec[] = [
  { id: 'case-summary', tKey: 'caseSummary', icon: <FileSignature /> },
]

/** Width of the fade that stands in for the rail's (hidden) scrollbar. */
const RAIL_FADE = '2rem'

/** Fades out whichever rail edge is clipping content, so overflow reads as
 * "there is more" without a scrollbar eating a chunk of a 76px column. */
function railFadeMask({ hasAbove, hasBelow }: ScrollEdges): string | undefined {
  if (!hasAbove && !hasBelow) return undefined
  const stops = [
    hasAbove ? `transparent 0, black ${RAIL_FADE}` : 'black 0',
    hasBelow ? `black calc(100% - ${RAIL_FADE}), transparent 100%` : 'black 100%',
  ]
  return `linear-gradient(to bottom, ${stops.join(', ')})`
}

function railChipInitials(name: string | undefined): string {
  // Letters/digits only, so "OSINT (2)" → "O2", not "O(".
  const words = (name ?? '')
    .split(/\s+/)
    .map(w => w.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter(Boolean)
  if (words.length === 0) return 'EX'
  return words
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase()
}

/** Rail icon for the active investigation — mirrors the case chip one level
 * down: initials tile plus the same status dot the top-bar chip uses. */
function RailInvestigationChipIcon({ snapshot }: { snapshot: ViewpointSnapshot }) {
  const pulses = snapshot.mode === 'exploration' || snapshot.mode === 'modified'
  return (
    <span className="relative flex size-6 shrink-0 items-center justify-center rounded-md bg-primary/15 text-[9px] font-bold text-primary">
      {railChipInitials(snapshot.investigationName)}
      {pulses ? (
        <PulsingDot tone="warning" size="sm" className="absolute -right-1 -top-1" />
      ) : (
        <StatusDot status="active" className="absolute -right-1 -top-1 size-2" />
      )}
    </span>
  )
}

function useActiveCase(caseId: string | undefined) {
  const query = useQuery<CaseSummary[]>({
    queryKey: caseKeys.list,
    queryFn: () => api.cases.list() as Promise<CaseSummary[]>,
    enabled: !!caseId,
  })
  return caseId ? query.data?.find(c => c.id === caseId) : undefined
}

type RailBadge = Pick<NavigationRailItem, 'badge' | 'badgeLabel'>

/** Count chips for the case views that have something waiting on the analyst. */
function useCaseRailBadges(caseId: string | undefined): (id: string) => RailBadge {
  const { t } = useTranslation()
  const pending = usePendingResolutionCount(caseId)
  // Photo matching is Advanced Face Detection's (ADR-0051): with it off the
  // count reads nothing and the badge is the duplicates' alone, as before AFD.
  const faceDetection = useEnabledExtensionIds().has(ADVANCED_FACE_DETECTION_EXTENSION_ID)
  const pendingFaceCount = usePendingFaceMatchCount(caseId ?? '')
  // Tattoo matching and Voice similarity are HUMINT's: with it off their counts are never read
  // and answer null, and the badge has no third or fourth part.
  const tattooLeads = useTattooLeadCount(caseId ?? '')
  const voiceLeads = useVoiceLeadCount(caseId ?? '')
  const schemaStatus = useCaseSchemaStatus(caseId)
  // The bell (PRD-0075 R-B6): alerts raised since the reader last opened the case's Monitor.
  const unreadAlerts = useMonitorUnreadCount(caseId)
  const resolution = entityResolutionBadge(t, {
    duplicates: pending.data?.candidates ?? 0,
    faces: faceDetection ? pendingFaceCount : null,
    tattoos: tattooLeads?.total ?? null,
    voices: voiceLeads?.total ?? null,
  })
  return id => {
    if (id === 'entity-resolution') return { badge: resolution.count, badgeLabel: resolution.label }
    if (id === 'monitor') {
      return {
        badge: unreadAlerts,
        badgeLabel: t('cases.monitor.bell.unread', { count: unreadAlerts }),
      }
    }
    if (id === 'schema' && schemaStatus.needsSchema) {
      return { badge: 1, badgeLabel: t('schemaAgent.sources.needsSchema') }
    }
    return {}
  }
}

export interface CaseRailNav {
  /** Case views, the investigation chip and its lenses, in rail order. */
  items: NavigationRailItem[]
  select: (id: string) => void
  caseLabel: string
  caseInitials: string
}

/**
 * Case-mode navigation data — the single source for both the desktop rail and
 * the phone drawer (`MobileNav`), so the two never drift apart.
 */
export function useCaseRailNav(caseId: string | undefined): CaseRailNav {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const enabledExtensions = useEnabledExtensionIds()
  const badgeFor = useCaseRailBadges(caseId)
  const snapshot = useViewpointSnapshot(caseId)
  const { data: caseSettings } = useCaseSettings(caseId ?? '')
  const activeCase = useActiveCase(caseId)
  const hasViewpoint = snapshot.mode !== 'empty'

  const toItem = (spec: CaseItemSpec): NavigationRailItem => ({
    id: spec.id,
    label: t(`nav.subviews.${spec.tKey}`),
    icon: spec.icon,
    groupEnd: spec.groupEnd,
    nested: spec.nested,
    nonNavigable: spec.nonNavigable,
    href: !spec.nonNavigable && caseId ? `/cases/${caseId}/${spec.id}` : undefined,
    ...badgeFor(spec.id),
  })

  // With an active viewpoint the lenses nest under the investigation chip, and
  // the chip stays visible on every case page as the persistent "you are
  // working in X" signal. With none, the investigations layer is just its
  // list entry — discovery (Research / OSINT) lives a level up, so nothing
  // about starting work is gated.
  const investigationChipItem: NavigationRailItem = {
    id: ACTIVE_INVESTIGATION_ID,
    label: snapshot.investigationName?.trim() || t('investigations.graph.chip.untitledExploration'),
    icon: <RailInvestigationChipIcon snapshot={snapshot} />,
    // The lenses are the point of having a viewpoint — they show as soon as
    // the chip does, on every case page, not only once one of them is open.
    alwaysOpen: true,
  }

  const items: NavigationRailItem[] = [
    ...enabledCaseDataSpecs(enabledExtensions, hiddenSubViewPaths(caseSettings)).map(toItem),
    ...(hasViewpoint
      ? [
          toItem(INVESTIGATIONS_SPEC),
          investigationChipItem,
          ...LENS_SPECS.map(spec => toItem({ ...spec, nested: true })),
        ]
      : [toItem({ ...INVESTIGATIONS_SPEC, groupEnd: true })]),
    ...OUTPUT_SPECS.map(toItem),
  ]

  const select = (id: string) => {
    if (!caseId) return
    if (id === ACTIVE_INVESTIGATION_ID) {
      // Saved investigation → its detail page; unsaved exploration has no
      // detail, so land on the graph lens where the working set lives.
      if (snapshot.activeId) navigate(`/cases/${caseId}/investigations/${snapshot.activeId}`)
      else navigate(`/cases/${caseId}/graph`)
      return
    }
    navigate(`/cases/${caseId}/${id}`)
  }

  const caseLabel = activeCase?.name ?? caseId ?? ''
  const caseInitials = caseLabel ? caseLabel.slice(0, 2).toUpperCase() : '??'
  return { items, select, caseLabel, caseInitials }
}

export interface GlobalRailNav {
  items: NavigationRailItem[]
  select: (id: string) => void
  /** Sections this user may see in the rail, in rail order. */
  visibleSections: Section[]
}

/** Global-mode navigation data (the product sections), shared by rail and drawer. */
export function useGlobalRailNav(onSectionClick: (section: Section) => void): GlobalRailNav {
  const { t } = useTranslation()
  const { user } = useAuth()
  const visibleSections = SECTIONS.filter(
    s => !s.hideFromRail && (!s.minRole || hasMinRole(user?.role, s.minRole))
  )
  const items: NavigationRailItem[] = visibleSections.map(s => {
    const Icon = s.icon
    return {
      id: s.key,
      label: t(`nav.sections.${s.key}`),
      icon: RAIL_ICON[s.key] ?? <Icon />,
      groupEnd: GROUP_END[s.key],
      href: s.defaultRoute,
    }
  })
  const select = (id: string) => {
    const section = visibleSections.find(s => s.key === id)
    if (section) onSectionClick(section)
  }
  return { items, select, visibleSections }
}

/** Opens the Sentry feedback form; `null` when Sentry is not configured. */
export function useFeedbackForm(): (() => Promise<void>) | null {
  const client = Sentry.getClient()
  if (!client) return null
  return async () => {
    const feedback =
      client.getIntegrationByName<ReturnType<typeof Sentry.feedbackIntegration>>('Feedback')
    if (feedback) {
      const form = await feedback.createForm()
      form.appendToDom()
      form.open()
    }
  }
}

export function IconRail({
  activeSection,
  onSectionClick,
  onSectionHover,
  caseId,
  activeSubViewPath,
}: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { theme, setTheme, railLabels } = useTheme()
  const railScrollRef = useRef<HTMLDivElement>(null)
  const railEdges = useScrollEdges(railScrollRef)
  const isCaseMode = !!caseId
  const {
    items: caseItems,
    select: handleCaseSelect,
    caseLabel,
    caseInitials,
  } = useCaseRailNav(caseId)
  const {
    items: globalItems,
    select: handleGlobalSelect,
    visibleSections,
  } = useGlobalRailNav(onSectionClick)
  const handleFeedback = useFeedbackForm()

  return (
    <TooltipProvider delayDuration={400}>
      <aside
        className="relative hidden h-full w-[76px] shrink-0 flex-col items-center gap-2 border-r border-border bg-sidebar pb-4 md:flex"
        onMouseLeave={() => onSectionHover(null)}
      >
        <RailBrand />
        <div
          ref={railScrollRef}
          // `min-h-0` lets the flex child shrink below its content so the rail
          // scrolls instead of pushing the bottom actions off-screen when the
          // case has more items than the viewport is tall.
          className="flex w-full min-h-0 flex-1 flex-col items-center overflow-y-auto overflow-x-hidden px-0.5 pt-2 scrollbar-none"
          style={{ maskImage: railFadeMask(railEdges) }}
          onMouseEnter={e => {
            // Only forward hover in global mode — case mode has no peek sidebar.
            if (isCaseMode) return
            const btn = (e.target as HTMLElement).closest('[data-slot="navigation-rail-item"]')
            if (!btn) return
            const id = btn.getAttribute('aria-label')
            const s = visibleSections.find(v => t(`nav.sections.${v.key}`) === id)
            if (s) onSectionHover(s)
          }}
        >
          {isCaseMode ? (
            <NavigationRail
              variant="static"
              items={caseItems}
              activeId={activeSubViewPath}
              onSelect={handleCaseSelect}
              showLabels={railLabels}
              header={
                <NavigationRailCaseChip
                  label={caseLabel}
                  initials={caseInitials}
                  hoverIcon={<Layers />}
                  backLabel={t('nav.cases.backToCases')}
                  href={HOME_ROUTE}
                  onBack={() => navigate(HOME_ROUTE)}
                />
              }
              aria-label={t('nav.caseNavigation')}
              className="!h-auto !w-full !border-0 !bg-transparent !px-1.5 !py-0"
            />
          ) : (
            <NavigationRail
              variant="static"
              items={globalItems}
              activeId={activeSection}
              onSelect={handleGlobalSelect}
              showLabels={railLabels}
              aria-label={t('nav.sections.cases')}
              className="!h-auto !w-full !border-0 !bg-transparent !px-1.5 !py-0"
            />
          )}
        </div>

        {isCaseMode && (
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href={`/cases/${caseId}/settings`}
                onClick={e => spaClick(e, () => navigate(`/cases/${caseId}/settings`))}
                aria-label={t('nav.cases.caseSettings')}
                aria-current={activeSubViewPath === 'settings' ? 'page' : undefined}
                className={
                  activeSubViewPath === 'settings'
                    ? 'flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring'
                    : 'flex size-10 shrink-0 items-center justify-center rounded-xl text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring'
                }
              >
                <Settings className="size-5" />
              </a>
            </TooltipTrigger>
            <TooltipContent side="right">{t('nav.cases.caseSettings')}</TooltipContent>
          </Tooltip>
        )}

        {handleFeedback && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={handleFeedback}
                className="flex size-10 shrink-0 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                aria-label={t('nav.feedback')}
              >
                <Bug className="size-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">{t('nav.feedback')}</TooltipContent>
          </Tooltip>
        )}

        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label={t('nav.userMenu')}
                  className="flex size-10 shrink-0 items-center justify-center rounded-xl text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <User className="size-5" />
                </button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="right">{user?.username || 'Account'}</TooltipContent>
          </Tooltip>
          <DropdownMenuContent side="right" align="end" className="min-w-56">
            <DropdownMenuLabel className="flex items-center gap-2">
              {user?.username || 'User'}
              {isAdmin(user?.role) && (
                <span className="rounded bg-warning-surface px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-warning-foreground">
                  {user?.role}
                </span>
              )}
              <ChevronsUpDown className="ml-auto h-3 w-3 text-muted-foreground" />
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={e => {
                e.preventDefault()
                setTheme(theme === 'dark' ? 'light' : 'dark')
              }}
            >
              {theme === 'dark' ? (
                <Sun className="mr-2 h-4 w-4" />
              ) : (
                <Moon className="mr-2 h-4 w-4" />
              )}
              {theme === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => logout()} className="text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              {t('nav.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </aside>
    </TooltipProvider>
  )
}
